import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { CHARACTER_META, speakLine, stopAllAudio, unlockAudio } from "@/lib/audio";
import { Bell, X, Send, Loader2, MessageCircle } from "lucide-react";

/**
 * Grid Concierge — global floating Q&A assistant.
 *
 * Chooses Amanda for /training and /, Alfred for all other routes.
 * Uses the same TTS pipeline as the rest of the app (respects the global
 * audio singleton lock — the concierge silences any other voice before
 * speaking and stops itself when the panel closes).
 */

const AMANDA_ROUTES = ["/", "/training"];
const WHATSAPP_URL = "https://wa.me/15678761419";

function characterForRoute(pathname) {
  return AMANDA_ROUTES.includes(pathname) ? "amanda" : "alfred";
}

function readStoredIndustry() {
  try {
    return sessionStorage.getItem("grid_industry_tag") || "";
  } catch (_) {
    return "";
  }
}

const GREETINGS = {
  amanda: "Hi there — I'm Amanda, your Grid Concierge. What can I clarify for you today?",
  alfred: "Good to see you. I'm Alfred, your Grid Concierge. How can I assist?",
};

function greetingFor(character, industry) {
  const base = GREETINGS[character];
  if (industry) {
    if (character === "amanda") {
      return `Hi there — I'm Amanda. I've got your ${industry} file open. What can I clarify?`;
    }
    return `Good to see you. I'm Alfred. I've pulled the ${industry} playbook. How can I assist?`;
  }
  return base;
}

// Vertical cockpit routes render their own Stationary Amanda pin
// (see VerticalCockpit.jsx). We suppress GridConcierge on those
// paths to avoid two floating concierge widgets fighting for the
// bottom-right corner. The induction contract-signing page is also
// locked — no global concierge inside the handshake.
const LOCKED_ROUTES = [
  "/green-shield",
  "/uprising",
  "/moto",
  "/sims",
  "/mobile-cycle",
  "/magic-garage",
  "/mixology",
  "/msquared",
  "/cierra",
  "/founders",
  "/affordable",
  "/induction",
];

export default function GridConcierge() {
  const location = useLocation();

  // Bail out entirely if we're inside a locked vertical cockpit or
  // the induction flow. This must sit BEFORE any hooks that follow
  // so the early-return doesn't change hook order between renders.
  const isLocked = LOCKED_ROUTES.some((r) => location.pathname === r || location.pathname.startsWith(`${r}/`));

  const character = characterForRoute(location.pathname);
  const meta = CHARACTER_META[character] || CHARACTER_META.amanda;

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [messages, setMessages] = useState([]);        // { role, text, escalate }
  const [industry, setIndustry] = useState("");
  const scrollerRef = useRef(null);

  // Speak the greeting once per panel open, per character.
  useEffect(() => {
    if (!open) return undefined;
    const ind = readStoredIndustry();
    setIndustry(ind);
    const line = greetingFor(character, ind);
    // Reset the transcript to a fresh greeting each time the panel opens.
    setMessages([{ role: "assistant", text: line, escalate: false }]);
    // Only speak if the user has already unlocked audio elsewhere — the
    // TTS layer will silently drop the play if not.
    try { speakLine(character, line); } catch (_) { /* noop */ }
    return () => stopAllAudio();
  }, [open, character]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, pending]);

  const toggle = () => {
    if (!open) unlockAudio();
    setOpen((v) => !v);
  };

  const ask = async (e) => {
    e?.preventDefault?.();
    const q = input.trim();
    if (!q || pending) return;
    setInput("");
    // Keep the transcript to a rolling 10 messages.
    setMessages((m) => [...m, { role: "user", text: q }].slice(-10));
    setPending(true);
    try {
      const { data } = await api.post("/concierge/ask", {
        question: q,
        character,
        route: location.pathname,
        industry: industry || undefined,
      });
      const answer = data.answer || "";
      const escalate = !!data.escalate;
      setMessages((m) => [...m, { role: "assistant", text: answer, escalate }].slice(-10));
      try { speakLine(character, answer); } catch (_) { /* noop */ }
    } catch (err) {
      const msg = err?.response?.data?.detail || "I hit a snag. Try the WhatsApp line below.";
      setMessages((m) => [...m, { role: "assistant", text: msg, escalate: true }].slice(-10));
    } finally {
      setPending(false);
    }
  };

  // Hooks are all run above (rules-of-hooks safe). Now short-circuit
  // on locked vertical routes so the concierge widget vanishes.
  if (isLocked) return null;

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        onClick={toggle}
        aria-label={open ? "Close concierge" : "Open Grid Concierge"}
        data-testid="grid-concierge-trigger"
        className="fixed z-[80] rounded-full flex items-center justify-center transition-transform"
        style={{
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          background:
            "radial-gradient(circle at 30% 30%, #F5C842 0%, #D4AF37 55%, #B08920 100%)",
          border: "1px solid rgba(255,255,255,0.35)",
          boxShadow:
            "0 0 22px rgba(212,175,55,0.55), 0 10px 24px -8px rgba(0,0,0,0.6)",
          color: "#1F1300",
          animation: open ? "none" : "concierge-pulse 2.8s ease-in-out infinite",
          transform: open ? "scale(0.9)" : "scale(1)",
        }}
      >
        {open ? <X size={22} /> : <Bell size={22} />}
      </button>

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label={`${meta.name} — Grid Concierge`}
          data-testid="grid-concierge-panel"
          className="fixed z-[79] rounded-sm border overflow-hidden flex flex-col"
          style={{
            bottom: 92,
            right: 24,
            left: 16,
            maxWidth: 380,
            width: "auto",
            maxHeight: 520,
            marginLeft: "auto",
            borderColor: "rgba(212,175,55,0.5)",
            background:
              "linear-gradient(160deg, rgba(6,11,20,0.97) 0%, rgba(16,185,129,0.06) 60%, rgba(212,175,55,0.10) 100%)",
            boxShadow:
              "0 30px 60px -10px rgba(0,0,0,0.7), 0 0 24px rgba(212,175,55,0.25)",
            backdropFilter: "blur(10px)",
            animation: "concierge-rise 260ms ease-out",
          }}
        >
          {/* Header */}
          <div
            className="px-4 py-3 border-b flex items-center gap-3"
            style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(4,7,12,0.5)" }}
          >
            <div
              className="rounded-full overflow-hidden flex-shrink-0"
              style={{
                width: 46,
                height: 46,
                padding: 2,
                background:
                  "conic-gradient(from 0deg, #D4AF37, #34D399, #D4AF37, #34D399)",
              }}
              aria-hidden
            >
              <div className="w-full h-full rounded-full overflow-hidden" style={{ background: "#04070C" }}>
                <img
                  src={meta.avatar}
                  alt={meta.name}
                  className="w-full h-full object-cover"
                  data-testid={`concierge-avatar-${character}`}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[9px] uppercase tracking-[0.28em] text-emerald-300/80 font-mono-aviation">
                Grid Concierge · Live
              </div>
              <div className="font-display text-white text-base leading-tight">
                {meta.name}
              </div>
            </div>
            <button
              type="button"
              onClick={toggle}
              aria-label="Close concierge"
              data-testid="grid-concierge-close"
              className="w-8 h-8 rounded-sm flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10"
            >
              <X size={14} />
            </button>
          </div>

          {/* Transcript */}
          <div
            ref={scrollerRef}
            data-testid="grid-concierge-transcript"
            className="flex-1 overflow-y-auto p-4 space-y-3"
            style={{ minHeight: 160 }}
          >
            {messages.map((m, i) => (
              <ConciergeMessage key={`m-${i}`} msg={m} character={character} />
            ))}
            {pending && <TypingDots />}
          </div>

          {/* Composer */}
          <form
            onSubmit={ask}
            className="p-3 border-t flex items-end gap-2"
            style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(4,7,12,0.55)" }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={pending}
              placeholder="Ask me anything about the Grid…"
              data-testid="grid-concierge-input"
              className="flex-1 bg-black/40 border border-white/10 focus:border-gold/70 outline-none px-3 py-2.5 rounded-sm text-white text-sm placeholder:text-slate-600 transition-colors"
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={pending || !input.trim()}
              data-testid="grid-concierge-send"
              className="btn-gold px-3 py-2.5 rounded-sm min-w-[44px] min-h-[44px] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Send question"
            >
              {pending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </form>
        </div>
      )}

      <style>{`
        @keyframes concierge-pulse {
          0%, 100% { box-shadow: 0 0 22px rgba(212,175,55,0.55), 0 10px 24px -8px rgba(0,0,0,0.6); }
          50%      { box-shadow: 0 0 34px rgba(212,175,55,0.85), 0 10px 30px -6px rgba(0,0,0,0.6); }
        }
        @keyframes concierge-rise {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        @keyframes concierge-dot {
          0%, 60%, 100% { transform: translateY(0);    opacity: 0.4; }
          30%           { transform: translateY(-4px); opacity: 1;   }
        }
        @media (max-width: 480px) {
          [data-testid="grid-concierge-trigger"] { width: 48px; height: 48px; }
        }
      `}</style>
    </>
  );
}

function ConciergeMessage({ msg, character }) {
  const isUser = msg.role === "user";
  const showEscalate = !isUser && msg.escalate;
  const bubbleStyle = isUser
    ? {
        background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(16,185,129,0.14))",
        border: "1px solid rgba(212,175,55,0.35)",
        color: "#F8FAFC",
      }
    : {
        background: "linear-gradient(160deg, rgba(6,11,20,0.7), rgba(6,11,20,0.4))",
        border: "1px solid rgba(52,211,153,0.22)",
        color: "#E2E8F0",
      };
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`concierge-msg-${msg.role}`}
    >
      <div className="max-w-[86%]">
        <div
          className="text-[11px] uppercase tracking-[0.24em] font-mono-aviation mb-1"
          style={{ color: isUser ? "#D4AF37" : "#34D399" }}
        >
          {isUser ? "You" : character.toUpperCase()}
        </div>
        <div className="rounded-sm px-3 py-2 text-[13px] leading-relaxed" style={bubbleStyle}>
          {msg.text}
        </div>
        {showEscalate && (
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="concierge-whatsapp-link"
            className="mt-2 inline-flex items-center gap-2 rounded-sm px-3 py-2 text-[11px] uppercase tracking-[0.22em]"
            style={{
              background: "linear-gradient(90deg, #25D366 0%, #128C7E 100%)",
              color: "#F8FAFC",
              fontFamily: "var(--font-mono-aviation, monospace)",
              boxShadow: "0 0 14px rgba(37,211,102,0.35)",
            }}
          >
            <MessageCircle size={12} /> Chat on WhatsApp
          </a>
        )}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex justify-start" data-testid="concierge-typing">
      <div className="flex items-center gap-1.5 rounded-sm px-3 py-2"
        style={{
          background: "rgba(6,11,20,0.5)",
          border: "1px solid rgba(52,211,153,0.22)",
        }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={`dot-${i}`}
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{
              background: "#34D399",
              animation: `concierge-dot 900ms ease-in-out ${i * 150}ms infinite`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
